import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { useDeferredValue, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DataTableSkeleton } from '@/components/common/DataTableSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadMoreButton } from '@/components/common/LoadMoreButton';
import { PageHeader } from '@/components/common/PageHeader';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { EstablecimientoConProtocolosDialog } from '../components/EstablecimientoConProtocolosDialog';
import { EstablecimientosTable } from '../components/EstablecimientosTable';
import { ProfesionalFiltroChip } from '../components/ProfesionalFiltroChip';
import {
  useDeleteEstablecimiento,
  useEstablecimientos,
  useEstablecimientosByProfesional,
} from '../hooks/useEstablecimientos';
import type { Establecimiento, EstablecimientoListParams } from '../types';

const ESTABLECIMIENTO_CON_PROTOCOLOS_ERROR = 'establecimiento_con_protocolos';

export function EstablecimientosPage(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const profesionalFiltro = searchParams.get('profesional');

  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [sortBy, setSortBy] =
    useState<NonNullable<EstablecimientoListParams['sort_by']>>('nombre');
  const [sortOrder, setSortOrder] =
    useState<NonNullable<EstablecimientoListParams['sort_order']>>('asc');

  const filteredQuery = useEstablecimientosByProfesional(
    profesionalFiltro ?? undefined,
    { limit: 50 }
  );

  const allQuery = useEstablecimientos(
    profesionalFiltro
      ? { limit: 0 }
      : {
          q: deferredSearch.trim() || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          limit: 100,
        }
  );

  const activeQuery = profesionalFiltro ? filteredQuery : allQuery;
  const {
    items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch,
  } = activeQuery;

  const [establecimientoToDelete, setEstablecimientoToDelete] =
    useState<Establecimiento | null>(null);
  const [conProtocolosDialog, setConProtocolosDialog] =
    useState<Establecimiento | null>(null);
  const deleteMutation = useDeleteEstablecimiento();

  function handleClearFiltroProfesional(): void {
    setSearchParams({});
  }

  function handleSortChange(
    newSortBy: NonNullable<EstablecimientoListParams['sort_by']>,
    newSortOrder: NonNullable<EstablecimientoListParams['sort_order']>
  ): void {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }

  async function handleConfirmDelete(): Promise<void> {
    if (!establecimientoToDelete) return;
    try {
      await deleteMutation.mutateAsync(establecimientoToDelete.uid_establecimiento);
      notify.success(`Establecimiento "${establecimientoToDelete.nombre}" eliminado.`);
      setEstablecimientoToDelete(null);
    } catch (err) {
      if (isApiError(err) && err.errorCode === ESTABLECIMIENTO_CON_PROTOCOLOS_ERROR) {
        setConProtocolosDialog(establecimientoToDelete);
        setEstablecimientoToDelete(null);
        return;
      }
      notify.fromError(err, 'Error al eliminar el establecimiento.');
    }
  }

  return (
    <>
      <PageHeader
        title="Establecimientos"
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/establecimientos/nuevo')}
          >
            Nuevo establecimiento
          </Button>
        }
      />

      <Stack spacing={3}>
        {!profesionalFiltro && (
          <TextField
            placeholder="Buscar por nombre o localidad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}

        {profesionalFiltro && (
          <Stack direction="row" spacing={1}>
            <ProfesionalFiltroChip
              uidProfesional={profesionalFiltro}
              onClear={handleClearFiltroProfesional}
            />
          </Stack>
        )}

        {isLoading && <DataTableSkeleton columns={4} rows={5} />}

        {isError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Reintentar
              </Button>
            }
          >
            {error instanceof Error
              ? error.message
              : 'Error al cargar los establecimientos.'}
          </Alert>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            icon={<BusinessIcon sx={{ fontSize: 'inherit' }} />}
            title={
              profesionalFiltro
                ? 'Este profesional no tiene establecimientos asignados'
                : deferredSearch.trim()
                  ? 'No hay resultados para tu búsqueda'
                  : 'No hay establecimientos todavía'
            }
            description={
              profesionalFiltro
                ? 'Cambiá el filtro o creá un establecimiento nuevo.'
                : deferredSearch.trim()
                  ? 'Probá con otros términos o limpiá la búsqueda.'
                  : 'Crea el primer establecimiento para empezar.'
            }
            action={
              !profesionalFiltro && !deferredSearch.trim() ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/establecimientos/nuevo')}
                >
                  Crear establecimiento
                </Button>
              ) : undefined
            }
          />
        )}

        {!isLoading && !isError && items.length > 0 && (
          <Box>
            <EstablecimientosTable
              items={items}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
              onDelete={(e) => setEstablecimientoToDelete(e)}
              sortingDisabled={!!profesionalFiltro}
            />
            <LoadMoreButton
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            />
          </Box>
        )}
      </Stack>

      {establecimientoToDelete && (
        <DeleteConfirmDialog
          open={true}
          nombre={establecimientoToDelete.nombre}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setEstablecimientoToDelete(null)}
          loading={deleteMutation.isPending}
        />
      )}

      {conProtocolosDialog && (
        <EstablecimientoConProtocolosDialog
          open={true}
          uidEstablecimiento={conProtocolosDialog.uid_establecimiento}
          nombre={conProtocolosDialog.nombre}
          onClose={() => setConProtocolosDialog(null)}
        />
      )}
    </>
  );
}
